import { html, css, LitElement, unsafeCSS, adoptStyles } from 'lit';
// Vite: '?inline' returns the CSS as a string instead of injecting it into <head>.
// Webpack: use '?raw' (or an asset/source rule) instead.
import olCss from 'ol/ol.css?inline';

export class SearchMap extends LitElement {
    static styles = [
        css`
            *, ::after, ::before {
                box-sizing: border-box;
            }
            :host { 
                display: block; 
            }
            .map { 
                width: 100%; 
                height: 500px; 
                background: #eee; 
            }
            .ol-popup, .map, .ol-viewport {
                transition: none !important;
            }
            .tooltip {
                position: absolute;
                background-color: white;
                padding: 5px;
                border: 1px solid #ccc;
                border-radius: 4px;
                pointer-events: none;
                display: none;
            }
            .ol-full-screen button {
                cursor: pointer;
            }
            .ol-popup {
                position: absolute;
                background-color: white;
                padding: 15px;
                border-radius: 10px;
                border: 1px solid #fff;
                bottom: 12px;
                left: -50px;
                min-width: 232px;
                font-weight: 300;
                font-size: 13px;
                filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
            }
            .ol-popup::before,
            .ol-popup::after {
                border-top: 10px solid white;
                border-right: 10px solid transparent;
                border-left: 10px solid transparent;
                content: "";
                height: 0;
                width: 0;
                position: absolute;
                bottom: -9px;
                left: 40px;
            }
            .ol-popup-closer { 
                text-decoration: none; 
                position: absolute; 
                top: 2px; 
                right: 8px; 
                color: #333; 
            }
            .title {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 8px;
                line-height: 1rem;
            }
            a {
                color: #333;
                text-decoration: none;
            }
            a.g-link {
                margin-top: 8px;
                font-size: 13px;
                color: #005982;
                display: block;
            }
            .title a {
                color: #005982;
                text-decoration: underline;
            }
            .infowindow {
                max-height: 400px;
                overflow-y: auto;
            }
            .infowindow .item:not(:first-child) {
                margin-top: 30px;
            }
        `
    ];

    static get properties() {
        return {
            isLoading: { type: Boolean },
        };
    }

    constructor() {
        super();
        this.isLoading = true;
        this.map = null;
        this.data = [];
        this.olModules = null;
        this.infowindow = null;

        const searchEl = document.querySelector(".mapsearch");
        if (searchEl?.dataset.mappoints) {
            try {
                const parsed = JSON.parse(searchEl.dataset.mappoints);
                this.data = parsed.filter(d => d.includeInMapSearch);
            } catch (e) {
                console.error("Failed to parse map dataset:", e);
            }
        }

        this.markerSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="red" d="M256 0C160 0 82 78 82 174c0 101 80 164 123 239 42 75 42 102 51 102s9-27 51-99c43-75 123-138 123-239C430 78 352 0 256 0zm0 240c-37 0-67-30-67-67 0-37 30-67 67-67s67 30 67 67c0 37-30 67-37 67z"/><circle cx="256" cy="174" r="67" fill="rgba(0,0,0,0.01)"/></svg>`;
        this.markerSVGUrl = 'data:image/svg+xml;base64,' + btoa(this.markerSVG);
    }

    async loadOpenLayers() {
        if (this.olModules) return this.olModules;

        const [
            { Map, View },
            { default: OSM },
            { default: TileLayer },
            { Style, Icon },
            { default: Point },
            { default: VectorSource },
            { default: VectorLayer },
            { defaults: defaultInteractions },
            { default: Feature },
            { fromLonLat },
            { default: Overlay },
            { default: FullScreen },
            { defaults: defaultControls }
        ] = await Promise.all([
            import('ol'),
            import('ol/source/OSM.js'),
            import('ol/layer/Tile.js'),
            import('ol/style.js'),
            import('ol/geom/Point.js'),
            import('ol/source/Vector.js'),
            import('ol/layer/Vector.js'),
            import('ol/interaction.js'),
            import('ol/Feature.js'),
            import('ol/proj.js'),
            import('ol/Overlay.js'),
            import('ol/control/FullScreen.js'),
            import('ol/control/defaults.js')
        ]);

        this.olModules = {
            Map, View, OSM, TileLayer, Style, Icon, Point,
            VectorSource, VectorLayer, defaultInteractions, Feature,
            fromLonLat, Overlay, FullScreen, defaultControls
        };

        return this.olModules;
    }

    async firstUpdated() {
        // Apply OpenLayers CSS to this shadow root. OL's styles come first so
        // the component's own overrides (e.g. .ol-popup) take precedence.
        adoptStyles(this.shadowRoot, [
            unsafeCSS(olCss),
            ...SearchMap.elementStyles,
        ]);

        if (!this.data || this.data.length === 0) {
            this.isLoading = false;
            return;
        }

        const ol = await this.loadOpenLayers();
        const mapContainer = this.shadowRoot.querySelector('.map');

        this.markerStyle = new ol.Style({
            image: new ol.Icon({
                anchor: [0.5, 1],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                src: this.markerSVGUrl,
                scale: 0.08
            }),
        });

        this.markerLayer = new ol.VectorLayer({
            source: new ol.VectorSource(),
            declutter: false,
            renderOptions: {
                willReadFrequently: true
            }
        });

        this.map = new ol.Map({
            target: mapContainer,
            interactions: ol.defaultInteractions({ mouseWheelZoom: false }),
            controls: ol.defaultControls().extend([new ol.FullScreen()]),
            layers: [
                new ol.TileLayer({
                    source: new ol.OSM(),
                }),
                this.markerLayer
            ],
            view: new ol.View({
                center: ol.fromLonLat([175.0032646, -40.91553427]),
                zoom: 12,
                maxZoom: 20
            }),
        });

        this.setupInfoWindow(ol);
        this.setupMarkers(ol);

        this.map.on('singleclick', (evt) => {
            const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);
            const source = this.markerLayer.getSource();
            this.handleMapClick(source, feature);
        });

        this.isLoading = false;
    }

    setupInfoWindow(ol) {
        const container = this.shadowRoot.getElementById('popup');
        this.infocontent = this.shadowRoot.getElementById('popup-content');
        const closer = this.shadowRoot.getElementById('popup-closer');

        this.infowindow = new ol.Overlay({
            element: container,
            autoPanMargin: 150,
            offset: [0, -15],
            autoPan: {
                animation: { duration: 250 }
            }
        });

        this.map.addOverlay(this.infowindow);

        closer.onclick = (e) => {
            e.preventDefault();
            this.closePopup();
            return false;
        };
    }

    setupMarkers(ol) {
        const source = this.markerLayer.getSource();

        this.data.forEach((loc) => {
            const lng = Number(loc.longitude);
            const lat = Number(loc.latitude);
            if (isNaN(lng) || isNaN(lat)) return;

            const coords = ol.fromLonLat([lng, lat]);
            const marker = new ol.Feature({
                geometry: new ol.Point(coords),
                location: loc
            });
            marker.setStyle(this.markerStyle);
            source.addFeature(marker);
        });

        requestAnimationFrame(() => this.fitBounds());
    }

    fitBounds() {
        if (!this.map || !this.markerLayer) return;
        this.map.updateSize();

        const extent = this.markerLayer.getSource().getExtent();
        if (!extent || extent[0] === Infinity) return;

        this.map.getView().fit(extent, {
            minResolution: 5,
            maxZoom: 16,
            padding: [80, 50, 50, 50],
        });
    }

    closePopup() {
        if (this.infowindow) {
            this.infowindow.setPosition(undefined);
        }
    }

    getInfoWindowContent(features) {
        return features.map(feature => {
            const location = feature.get('location');
            if (!location) return "";

            const titleHtml = `<div class="title"><a target="_self" href="${encodeURI(location.pageUrl)}" tabindex="0">${this.escapeHTML(location.name)}</a></div>`;
            const imageHtml = location.image ? `<div><img style="display:block;width:190px;height:120px;object-fit: cover;" src="${encodeURI(location.image)}" alt="${this.escapeHTML(location.name)}" /></div>` : ``;
            const googleHtml = `<a class="g-link" target="_blank" rel="noopener noreferrer" href="https://maps.google.com/maps?q=${location.latitude}+${location.longitude}">Open in Google Maps</a>`;

            return `
                <div class="item">
                    ${titleHtml}
                    ${imageHtml}
                    ${googleHtml}
                </div>`;
        }).join('');
    }

    handleMapClick(source, feature) {
        if (feature) {
            const coordinates = feature.getGeometry().getCoordinates();
            const matchingFeatures = source.getFeaturesAtCoordinate(coordinates);

            this.infocontent.innerHTML = this.getInfoWindowContent(matchingFeatures);
            this.infowindow.setPosition(coordinates);
        } else {
            this.closePopup();
        }
    }

    escapeHTML(str) {
        if (!str) return "";
        return String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    disconnectedCallback() {
        if (this.map) {
            this.map.setTarget(null);
            this.map = null;
        }
        super.disconnectedCallback();
    }

    render() {
        return html`
            <div style="position:relative">
                <div class="map" style="${this.isLoading ? 'display: none;' : 'display: block;'}"></div>
                <div id="tooltip" class="tooltip"></div>
                <div id="popup" class="ol-popup infomarker">
                    <a href="#" id="popup-closer" class="ol-popup-closer">✖</a>
                    <div id="popup-content"></div>
                </div>
            </div>
        `;
    }
}

customElements.define('search-map', SearchMap);
